<template>
  <div>
    <svg
      ref="svg"
      width="1000"
      height="700"
      viewBox="0 0 1000 700"
      preserveAspectRatio="xMidYMid meet"
      class="net-relations"
    >
      <template v-for="connection in zIndexedConnections" :key="connection.uid">
        <net-relation
          v-if="connection.type == 'relation'"
          :id="'net-relation-' + connection.id"
          :lines="optimizedPathLines[connection.uid]"
          :relation-id="connection.id"
          :selected="selection.selected.has(connection.uid) || draftRelation?.uid == connection.uid"
          :connecting="connectingRelationIds.has(connection.id)"
          :highlighted="selection.highlighted.has(connection.uid)"
          :incoming="
            showSelectedNetItemConnections && incomingRelationIds.has(connection.id)
          "
          :outgoing="
            showSelectedNetItemConnections && outgoingRelationIds.has(connection.id)
          "
          :fade="fade"
          :endpoints-only="sceneTree.activeScene.linkRelationsDisplay == 'endpoints'"
          :non="connection.type == 'relation' && connection.updateType == 'none'"
          :clickable="clickable"
          @select="select"
          @add-select="addSelection($event)"
          @contextmenu="onContextMenu"
        />
  
        <net-flow
          v-else
          :id="'net-flow-' + connection.id"
          :lines="optimizedPathLines[connection.uid]"
          :flow-branch-id="connection.id"
          :fade="fade"
          :value="connection.groupId != null ? Number(elementValues[connection.groupId]?.value ?? 0) : undefined"
          :endpoints-only="sceneTree.activeScene.flowRelationsDisplay == 'endpoints'"
          :selected="selection.selected.has(`flow_${connection.groupId ?? -1}`) || draftFlow?.uid == connection.uid"
          :branch-type="connection.branchType"
          :clickable="clickable"
          @select="select($event)"
          @add-select="addSelection($event)"
          @contextmenu="onContextMenu"
        />
      </template>
  
      <template v-for="(compactGroup, uid) in compactRelationGroups" :key="uid">
        <template v-if="compactGroup.outgoing.relations.length > 0">
          <path
            class="net-relations__compactlink"
            :d="`M${itemSizes[uid].right},${itemSizes[uid].center.y} L${itemSizes[uid].right + 12},${itemSizes[uid].center.y}`"
            :style="`stroke-width: ${Math.max(compactGroup.outgoing.influenceSum * 10, 1)}px`"
            :class="{ '-unclickable': !clickable }"
          />
          <path
            class="net-relations__arrow"
            :d="`M${itemSizes[uid].right + 20},${itemSizes[uid].center.y} L${itemSizes[uid].right + 10},${itemSizes[uid].center.y - compactGroup.outgoing.influenceSum * 5 - 4} L${itemSizes[uid].right + 10 },${itemSizes[uid].center.y + compactGroup.outgoing.influenceSum * 5 + 4} Z`"
          />
        </template>
        <template v-if="compactGroup.incoming.relations.length > 0">
          <path
            class="net-relations__compactlink"
            :d="`M${itemSizes[uid].left - 20},${itemSizes[uid].center.y} L${itemSizes[uid].left - 8},${itemSizes[uid].center.y}`"
            :style="`stroke-width: ${Math.max(compactGroup.incoming.influenceSum * 10, 1)}px`"
            :class="{ '-unclickable': !clickable }"
          />
          <path
            class="net-relations__arrow"
            :d="`M${itemSizes[uid].left},${itemSizes[uid].center.y} L${itemSizes[uid].left - 10},${itemSizes[uid].center.y - compactGroup.incoming.influenceSum * 5 - 4} L${itemSizes[uid].left - 10},${itemSizes[uid].center.y + compactGroup.incoming.influenceSum * 5 + 4} Z`"
            :class="{ '-unclickable': !clickable }"
          />
        </template>
      </template>
    </svg>

    <template v-if="netMode == 'edit'">
      <relation-draft-popup-menu
        v-if="draftRelation"
        :x="draftRelationLineAnchor.x"
        :y="draftRelationLineAnchor.y"
        @close="closePopupmenu"
      ></relation-draft-popup-menu>

      <flow-draft-popup-menu
        v-if="draftFlow"
        :x="draftFlowLineAnchor.x"
        :y="draftFlowLineAnchor.y"
        @close="closePopupmenu"
      ></flow-draft-popup-menu>

      <relation-popup-menu
        v-if="popupmenu.relation"
        :relation-id="popupmenu.relation.id"
        :x="popupRelationLineCenter.x"
        :y="popupRelationLineCenter.y - 20"
        @close="closePopupmenu"
      ></relation-popup-menu>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, reactive, inject, onUnmounted } from "vue";
import { filter, mapValues, pickBy } from "@/helpers/utility/CollectionFunctions";
import usePathsUpdater from "./usePathsUpdater.js";
import eventHub from "@/helpers/utility/Events.js";

import NetRelation from "./NetRelation.vue";
import NetFlow from "../flow/NetFlow.vue";

import RelationPopupMenu from "./RelationPopupMenu.vue";
import RelationDraftPopupMenu from "./RelationDraftPopupMenu.vue";
import FlowDraftPopupMenu from "@editor/items/flow/FlowDraftPopupMenu.vue";

import { useRelationStore } from "@editor/items/relation/relations.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useRelationSelectionPoints } from "./useRelationSelectionPoints.js";

import type { RelationItem } from "@editor/items/relation/RelationItem";
import type { FlowBranchItem } from "../flow/FlowBranchItem";
import type { FlowItem } from "../flow/FlowItem";
import type { ExpressionValue } from "../expression/expressionFunctions.js";
import type { CanvasSelection } from "@editor/net/NetCanvasProps.js";
import type { SceneTreeItemSize } from "../scene/SceneTreeItem";

import { isUidOfType, splitUid, toUid, type Uid } from "@/helpers/utility/Id.js";
import { useNetCoords } from "@editor/net/netCoordsModifier.js";
import { keyCanvasDimensions } from "@editor/net/netCanvasInjectionKeys.js";
import { getLineCenter } from "./relationFunctions.js";
import { useSceneTree } from "../scene/sceneTree.js";
import { notNull } from "@/helpers/utility/Type.js";
import { isElementTreeNode } from "../sceneElement/sceneElementFunctions.js";
import { useSceneRelationSettingsStore } from "./sceneRelationSettings.store.js";
import { useSceneFlowNodeStore } from "../flow/sceneFlowNodes.js";

type ConnectionItem = {
  uid: Uid<"relation" | "flowBranch">;
  id: number;
  groupId?: number;
  type: "relation" | "flowBranch";
  branchType?: "source" | "sink";
  updateType?: "change" | "none";
  maxInfluence?: number;

  startUid: Uid<"sceneNode" | "flow">;
  endUid: Uid<"sceneNode" | "flow">;
};

const sceneTree = useSceneTree();
const relationStore = useRelationStore();

/**
 * This element creates the data for drawing the different relations.
 *
 * process for connecting elements:
 * 1. order anchor points on elements by distance.
 * 1a. of the best two connections: find path that does not collide with elements
 * 2. spread connections on anchor points apart
 */

const props = withDefaults(defineProps<{
  relations: { [id: string]: RelationItem };
  flowBranches: { [id: string]: FlowBranchItem };
  itemSizes: { [uid: string]: SceneTreeItemSize };

  elementValues: { [uid: string]: ExpressionValue };

  selection: CanvasSelection;
  netMode: "choose" | "edit" | "view" | "influence";

  fade?: boolean;
  showSelectedNetItemConnections?: boolean;
  lastElementSizeChange?: number;
  
  clickable: boolean;

  impactSourceElement?:
    | { uid: Uid<"target" | "resource" | "index"> }
    | undefined;
  flowSourceElement?: { uid: Uid<"target" | "resource"> } | undefined;
  relationDraft?: { updateType?: "change" | "none" };
  elementAtMousePosition?: SceneTreeItemSize<"element"> | null;
}>(), {
  elementValues: () => ({}),

  fade: false,
  showSelectedNetItemConnections: true,
  lastElementSizeChange: undefined,
  clickable: true
});

const emit = defineEmits<{
  (event: "select", item: { uid: Uid<"relation" | "flowBranch" | "flow"> }): void;
  (event: "add-select", item: { uid: Uid<"relation" | "flowBranch" | "flow"> }): void;
  (event: "contextmenu", e: MouseEvent & { item?: RelationItem | FlowBranchItem | null }): void;
}>();

const canvasDimensions = inject(keyCanvasDimensions);

const { optimizedPathLines, updatePathlines } = usePathsUpdater();

const zIndexedConnections = computed(() => {
  return zIndexedRelationUids.value.map((uid) => visibleConnections.value[uid]).filter((connection) => connection != null);
});

const selectedRelationUids = computed((): Set<Uid<"relation">> => {
  return new Set([...props.selection.selected].filter((uid): uid is Uid<"relation"> => uid.startsWith("relation_")));
});
/**
 * @returns all relation ids, selected or highlighted first.
 */
const zIndexedRelationUids = computed((): Uid<"relation" | "flowBranch">[] => {
  return notMarkedRelationIds.value.concat([...markedRelationIds.value]);
});
const markedRelationIds = computed((): Set<Uid<"relation" | "flowBranch">> => {
  if (props.showSelectedNetItemConnections) {
    return new Set([
      ...selectedRelationUids.value,
      ...[...outgoingRelationIds.value].map((id) => toUid("relation", id)),
      ...[...incomingRelationIds.value].map((id) => toUid("relation", id)),
    ]);
  } else {
    return selectedRelationUids.value;
  }
});

const notMarkedRelationIds = computed(() => {
  // make a map to reduce iterations:
  const toRemoveMap = [...markedRelationIds.value].reduce(
    (memo, id) => ({
      ...memo,
      [id]: true
    }),
    {} as { [id: string]: boolean }
  );
  return connectionUids.value.filter((id) => toRemoveMap[id] !== true);
});

const connectionUids = computed(() => {
  return Object.values(visibleConnections.value).map((connection) => connection.uid);
});

const incomingRelationIds = computed(() => {
  const set = new Set<number>();
  if (draftRelation.value != null || draftFlow.value != null) return set;

  const incomingByUid = relationStore.activeByEndUid;

  for (const uid of props.selection.selected) {
    if (isUidOfType(uid, "sceneNode")) {
      const treeNode = sceneTree.treeNodes.get(uid);
      if (!treeNode) continue;
      if (!isElementTreeNode(treeNode)) continue;
      const node = treeNode.node;
      const elementUid = node.data.elementUid;
      const incomingRelations = incomingByUid[elementUid] ?? [];
      incomingRelations.forEach((rel) => set.add(rel.id));
      continue;
    }
    if (isUidOfType(uid, "target") || isUidOfType(uid, "resource")) {
      const incomingRelations = incomingByUid[uid] ?? [];
      incomingRelations.forEach((rel) => set.add(rel.id));
    }
  }

  return set;
});

const outgoingRelationIds = computed(() => {
  const set = new Set<number>();
  if (draftRelation.value != null || draftFlow.value != null) return set;

  const outgoingByUid = relationStore.activeByStartUid;

  for (const uid of props.selection.selected) {
    if (isUidOfType(uid, "sceneNode")) {
      const treeNode = sceneTree.treeNodes.get(uid);
      if (!treeNode) continue;
      if (!isElementTreeNode(treeNode)) continue;
      const node = treeNode.node;
      const elementUid = node.data.elementUid;
      const outgoingRelations = outgoingByUid[elementUid] ?? [];
      outgoingRelations.forEach((rel) => set.add(rel.id));
      continue;
    }
    if (isUidOfType(uid, "target") || isUidOfType(uid, "resource")) {
      const outgoingRelations = outgoingByUid[uid] ?? [];
      outgoingRelations.forEach((rel) => set.add(rel.id));
    }
  }

  return set;
});

const connectingRelationIds = computed(() => {
  const connectingRelationIds = new Set<number>();
  if (draftRelation.value != null || draftFlow.value != null) return connectingRelationIds;

  if (!props.showSelectedNetItemConnections) return connectingRelationIds;

  const incomingIds = incomingRelationIds.value;
  for (const relationId of outgoingRelationIds.value) {
    if (incomingIds.has(relationId)) connectingRelationIds.add(relationId);
  }

  return connectingRelationIds;
});

const roundPaths = computed(() => {
  return relationStore.roundPaths;
});

const spacePaths = computed(() => {
  return relationStore.spacePaths;
});

const deoverlapPaths = computed(() => {
  return relationStore.deoverlapPaths;
});

const activeRelations = computed(() => {
  return filter(props.relations, (relation) => relation.updateType != "none");
});

const relationsByUid = computed(() => {
  const relationsByUid: {
    [uid: string]: RelationItem;
  } = {};

  for (const relation of activeRelations.value) {
    relationsByUid[relation.uid] = relation;
  }

  if (draftRelation.value) {
    relationsByUid["relation_-1"] = draftRelation.value;
  }

  return relationsByUid;
});

/**
 * Filter out all relations that should not be shown:
 */
const existingRelations = computed((): { [uid: Uid<"relation">]: ConnectionItem } => {
  const connections = mapValues(relationsByUid.value, (relation): ConnectionItem | null => {
    const startElementUid = toUid(relation.startItemType, relation.startItemId);
    const endElementUid = toUid(relation.endItemType, relation.endItemId);
    const startNode = sceneTree.elementNodes.get(startElementUid);
    const endNode = sceneTree.elementNodes.get(endElementUid);

    // Exclude relations with elements without nodes:
    if (!startNode || !endNode) {
      return null;
    }
    // remove relations where any item is missing:
    if (
      props.itemSizes?.[startNode.uid] == null ||
      props.itemSizes?.[endNode.uid] == null
    ) {
      return null;
    }

    return {
      uid: relation.uid,
      id: relation.id,
      type: "relation",
      updateType: relation.updateType,

      maxInfluence: relation.maxInfluence,

      startUid: startNode.uid,
      endUid: endNode.uid
    };
  });
  return pickBy(
    connections,
    notNull
  ) as { [uid: Uid<"relation">]: ConnectionItem };
});

const sceneRelationSettingsStore = useSceneRelationSettingsStore();

const visibleRelations = computed((): { [uid: Uid<"relation">]: ConnectionItem } => {
  if (sceneTree.activeScene.linkRelationsDisplay == "hidden") {
    return {};
  }
  if (sceneTree.activeScene.linkRelationsDisplay == "compact") {
    return {};
  }
  const allRelations = existingRelations.value;
  return pickBy(allRelations, (relation) => {
    const sceneSettings = sceneRelationSettingsStore.byScene[sceneTree.activeScene.id]?.[relation.id];
    return sceneSettings?.visible ?? true;
  });
});

// ======================
// DRAFT

const draftRelation = computed((): RelationItem | null => {
  const sourceElement = props.impactSourceElement;

  if (!sourceElement) return null;
  if (!draftEndElement.value) return null;

  const { id: sourceId, type: sourceType } = splitUid(sourceElement.uid);

  const draftEndElementIdentity = splitUid(draftEndElement.value.data.elementUid);
  if (draftEndElementIdentity.type == "index") return null;

  return {
    id: -1,
    uid: "relation_-1",
    type: "relation",
    title: "",
    description: "",

    startItemId: sourceId,
    startItemType: sourceType,
    endItemId: draftEndElementIdentity.id,
    endItemType: draftEndElementIdentity.type,
    updateType: props.relationDraft?.updateType ?? "change",
    delay: 0,
    inverse: false,
    maxInfluence: 0,
    createdAt: Date.now(),
    curvePoints: [],
    curveType: "linear",
    formula: "x",
    docs: {}
  };
});
const draftRelationLineAnchor = computed(() => {
  if (draftRelation.value == null) return { x: 0, y: 0 };
  const lines = optimizedPathLines.value[draftRelation.value.uid] ?? [];
  if (lines.length == 0) return { x: 0, y: 0 };

  const lineCenter = getLineCenter(optimizedPathLines.value[draftRelation.value.uid]);

  let highestPoint = Infinity;
  for (const line of lines) {
    highestPoint = Math.min(highestPoint, line.start.y, line.end.y);
  }

  if (props.impactSourceElement) {
    const sourceElement = props.impactSourceElement;
    const top = props.itemSizes[sourceElement.uid]?.top;
    if (top != null && top < highestPoint) {
      highestPoint = top;
    }
  }

  if (draftEndElement.value && draftEndElement.value.top < highestPoint) {
    highestPoint = draftEndElement.value.top;
  }

  return {
    x: lineCenter.x,
    y: highestPoint
  };
});

// TODO: Add Flow draft visualization
const draftFlow = computed((): ConnectionItem | null => {
  const sourceElement = props.flowSourceElement;

  if (!sourceElement) return null;
  if (!draftEndElement.value) return null;

  const sourceElementNode = sceneTree.elementNodes.get(sourceElement.uid);
  if (!sourceElementNode) return null;

  return {
    id: -1,
    uid: "flowBranch_-1",
    type: "flowBranch",

    startUid: sourceElementNode.uid,
    endUid: draftEndElement.value.uid,

    branchType: "sink"
  };
});
const draftFlowLineAnchor = computed(() => {
  if (draftFlow.value == null) return { x: 0, y: 0 };
  const lines = optimizedPathLines.value[draftFlow.value.uid] ?? [];
  if (lines.length == 0) return { x: 0, y: 0 };

  const lineCenter = getLineCenter(optimizedPathLines.value[draftFlow.value.uid]);

  let highestPoint = Infinity;
  for (const line of lines) {
    highestPoint = Math.min(highestPoint, line.start.y, line.end.y);
  }

  if (props.impactSourceElement) {
    const sourceElement = props.impactSourceElement;
    const top = props.itemSizes[sourceElement.uid]?.top;
    if (top != null && top < highestPoint) {
      highestPoint = top;
    }
  }

  if (draftEndElement.value && draftEndElement.value.top < highestPoint) {
    highestPoint = draftEndElement.value.top;
  }

  return {
    x: lineCenter.x,
    y: highestPoint
  };
});

const draftEndElement = computed(
  (): SceneTreeItemSize<"element"> | null => {
    let startElement = props.impactSourceElement || props.flowSourceElement;

    if (!startElement) return null;

    let endElement: SceneTreeItemSize<"element"> | null = null;
    
    if (props.selection.selected.size == 1) {
      const first = props.selection.selected.values().next().value as string;
      const item = props.itemSizes[first];

      if (item?.type == "element") {
        endElement = item as SceneTreeItemSize<"element">;
      }
    }
    if (!endElement) {
      endElement = props.elementAtMousePosition ?? null;
    }

    if (!endElement) return null;
    const endElementData = endElement.data;
    if (endElementData.elementUid.startsWith("index")) return null;

    if (
      endElementData.elementUid == startElement.uid
    )
      return null;

    if (props.impactSourceElement) {
      const { id: sourceId, type: sourceType } = splitUid(props.impactSourceElement.uid);

      const matchingImpact = Object.values(props.relations).find(
        (relation) =>
          relation.startItemType == sourceType &&
          relation.startItemId == sourceId &&
          toUid(relation.endItemType, relation.endItemId) ==
            endElementData.elementUid
      );
      if (matchingImpact) return null;
    } else if (props.flowSourceElement) {
      const { id: sourceId, type: sourceType } = splitUid(props.flowSourceElement.uid);
      const sourceFlowBranches = Object.values(props.flowBranches).filter(
        (flowBranch) =>
          flowBranch.elementType == sourceType &&
          flowBranch.elementId == sourceId &&
          flowBranch.branchType == "source"
      );
      const endFlowBranches = Object.values(props.flowBranches).filter(
        (flowBranch) =>
          toUid(flowBranch.elementType, flowBranch.elementId) ==
            endElementData.elementUid &&
          flowBranch.branchType == "sink"
      );
      const matchingFlow = sourceFlowBranches.find((sourceBranch) =>
        endFlowBranches.find(
          (endBranch) => sourceBranch.flowId == endBranch.flowId
        )
      );
      if (matchingFlow) return null;
    }

    return endElement as SceneTreeItemSize<"element">;
  }
);

// ======================
// FLOW

const flowNodeStore = useSceneFlowNodeStore();

const flowConnections = computed(() => {
  const connections: { [uid: string]: ConnectionItem } = {};
  
  for (const flowBranch of Object.values(props.flowBranches)) {
    let startItemType: "target" | "resource" | "flow" = "flow";
    let startItemId = flowBranch.flowId;

    let endItemType: "target" | "resource" | "flow" = flowBranch.elementType;
    let endItemId = flowBranch.elementId;

    // turn around if source:
    if (flowBranch.branchType == "source") {
      startItemType = flowBranch.elementType;
      startItemId = flowBranch.elementId;

      endItemType = "flow";
      endItemId = flowBranch.flowId;
    }

    const startItemUid = toUid(startItemType, startItemId);
    const endItemUid = toUid(endItemType, endItemId);

    const sizeStartUid = getSizeUid(startItemUid);
    const sizeEndUid = getSizeUid(endItemUid);

    if (sizeStartUid == null || sizeEndUid == null) continue;
    if (props.itemSizes[sizeStartUid] == null || props.itemSizes[sizeEndUid] == null) continue;

    connections[flowBranch.uid] = {
      uid: flowBranch.uid,
      id: flowBranch.id,
      groupId: flowBranch.flowId,
      type: "flowBranch",
      branchType: flowBranch.branchType,

      startUid: sizeStartUid,
      endUid: sizeEndUid
    };
  }

  return connections;
});

function getSizeUid<T extends "target" | "resource" | "index" | "flow">(itemUid: Uid<T>): T extends "flow" ? Uid<"flow"> : (Uid<"sceneNode"> | null) {
  if (isUidOfType(itemUid, "flow")) {
    return itemUid as any;
  }
  const node = sceneTree.elementNodes.get(itemUid);
  if (!node) {
    return null as any;
  }
  return node.uid as any;
}

const visibleFlowConnections = computed((): { [uid: Uid<"flowBranch">]: ConnectionItem } => {
  if (sceneTree.activeScene.flowRelationsDisplay == "hidden" || sceneTree.activeScene.flowRelationsDisplay == "compact") {
    return {};
  }

  const connections = { ...flowConnections.value };

  if (draftFlow.value) {
    console.log("draftFlow.value: ", draftFlow.value);
    connections["flowBranch_-1"] = draftFlow.value;
  }

  return pickBy(connections, (connection) => {
    const flowNode = flowNodeStore.byScene[sceneTree.activeScene.id]?.[connection.groupId ?? -1];
    if (!flowNode) return true;
    return flowNode.visible;
  });
});

const visibleConnections = computed((): { [uid: Uid<"relation" | "flowBranch">]: ConnectionItem } => {
  return { ...visibleRelations.value, ...visibleFlowConnections.value };
});

const compactRelationGroups = computed(() => {
  const groups: {
    [itemUid: string]: {
      incoming: {
        relations: ConnectionItem[],
        influenceSum: number
      };
      outgoing: {
        relations: ConnectionItem[]
        influenceSum: number
      };
    };
  } = {};

  let maxOutgoingInfluenceSum = 0;
  let maxIncomingInfluenceSum = 0;

  for (const relation of Object.values(existingRelations.value)) {
    const displayMode = relation.type == "relation" ? sceneTree.activeScene.linkRelationsDisplay : sceneTree.activeScene.flowRelationsDisplay;
    if (displayMode != "compact") continue;

    const { startUid: startItemUid, endUid: endItemUid } = relation;

    if (groups[startItemUid] == null) {
      groups[startItemUid] = {
        incoming: {
          relations: [],
          influenceSum: 0
        },
        outgoing: {
          relations: [],
          influenceSum: 0
        }
      };
    }
    if (groups[endItemUid] == null) {
      groups[endItemUid] = {
        incoming: {
          relations: [],
          influenceSum: 0
        },
        outgoing: {
          relations: [],
          influenceSum: 0
        }
      };
    }
    groups[startItemUid].outgoing.relations.push(relation);
    groups[startItemUid].outgoing.influenceSum += Math.abs(relation.maxInfluence!);
    maxOutgoingInfluenceSum = Math.max(maxOutgoingInfluenceSum, groups[startItemUid].outgoing.influenceSum);
    groups[endItemUid].incoming.relations.push(relation);
    groups[endItemUid].incoming.influenceSum += Math.abs(relation.maxInfluence!);
    maxIncomingInfluenceSum = Math.max(maxIncomingInfluenceSum, groups[endItemUid].incoming.influenceSum);
  }

  // normalize influence sums:
  for (const group of Object.values(groups)) {
    group.incoming.influenceSum /= maxIncomingInfluenceSum;
    group.outgoing.influenceSum /= maxOutgoingInfluenceSum;
  }

  return groups;
});

const pathColors = computed(() => {
  const ps = useSettingsStore();
  const colors: {
    standard: string;
    faded: string;
    active: string;
    activeHue: number;

    incoming?: string;
    outgoing?: string;
    connecting?: string;
    inverse?: string;
    inverseActive?: string;
    inverseActiveHue?: number;
    inverseIncoming?: string;
    inverseOutgoing?: string;
    inverseConnecting?: string;
  } = {
    standard: ps.colorRelationStandard ?? "var(--color-relation-standard)",
    faded: ps.colorRelationFaded ?? "var(--color-relation-faded)",
    active: ps.colorRelationActive ?? "var(--color-action)",
    activeHue: ps.colorRelationActiveHue ?? ps.colorPrimaryHue ?? 207
  };

  colors.incoming = ps.colorRelationIncoming ?? colors.active;
  colors.outgoing = ps.colorRelationOutgoing ?? colors.active;
  colors.connecting = ps.colorRelationConnecting ?? colors.active;

  colors.inverse = ps.colorRelationInverse ?? colors.standard;
  colors.inverseActive = ps.colorRelationInverseActive ?? colors.active;
  colors.inverseActiveHue =
    ps.colorRelationInverseActiveHue ?? colors.activeHue;
  colors.inverseIncoming =
    ps.colorRelationInverseIncoming ??
    ps.colorRelationInverseActive ??
    colors.incoming;
  colors.inverseOutgoing =
    ps.colorRelationInverseOutgoing ??
    ps.colorRelationInverseActive ??
    colors.outgoing;
  colors.inverseConnecting =
    ps.colorRelationInverseConnecting ??
    ps.colorRelationInverseActive ??
    colors.connecting;

  return colors;
});

watch(() => props.lastElementSizeChange, () => {
  updateLines();
});
watch(connectionUids, () => {
  updateLines();
});
watch(roundPaths, () => {
  updateLines();
});
watch(spacePaths, () => {
  updateLines();
});
watch(deoverlapPaths, () => {
  updateLines();
});

const relevantItemSizes = computed(() => {
  const sizes: { [uid: string]: SceneTreeItemSize } = {};
  const types = ["element", "flow"];
  for (const uid in props.itemSizes) {
    if (types.includes(props.itemSizes[uid].type)) {
      sizes[uid] = props.itemSizes[uid];
    }
  }
  return sizes;
});

updateLines();

function updateLines() {
  updatePathlines(visibleConnections.value, relevantItemSizes.value, {
    round: roundPaths.value,
    space: spacePaths.value,
    deoverlap: deoverlapPaths.value
  });
}
function select(item: RelationItem | FlowItem, mouseEvent?: MouseEvent) {
  if (mouseEvent) {
    const globalPoint = {
      x: mouseEvent.clientX,
      y: mouseEvent.clientY
    };
    const { toLocal } = useNetCoords({ canvasDimensions });
    const localPoint = toLocal(globalPoint);
    selectionPoints.value.set(item.uid, localPoint);
  }
  emit("select", item);
}
function addSelection(item: RelationItem | FlowItem) {
  emit("add-select", item);
}
function onContextMenu(event: MouseEvent & { item?: RelationItem | FlowBranchItem | null }, item: RelationItem | FlowBranchItem | null) {
  event.item = item;
  emit("contextmenu", event);
}

const { selectionPoints } = useRelationSelectionPoints();

function onPopupmenuRequest(uid: Uid<string>) {
  const { type, id } = splitUid(uid);
  if (type == "relation") {
    const relation = props.relations[id];
    if (relation) {
      openPopupmenu(relation);
    }
  }
}

eventHub.on("popupmenu-request", onPopupmenuRequest);
onUnmounted(() => {
  eventHub.off("popupmenu-request", onPopupmenuRequest);
});

const popupmenu = reactive({
  relation: null as RelationItem | null
});

function openPopupmenu(relation: RelationItem) {
  popupmenu.relation = relation;
}
function closePopupmenu() {
  popupmenu.relation = null;
}

const popupRelationLineCenter = computed(() => {
  if (popupmenu.relation == null) return { x: 0, y: 0 };
  if (selectionPoints.value.has(popupmenu.relation.uid)) {
    return selectionPoints.value.get(popupmenu.relation.uid)!;
  }
  return getLineCenter(optimizedPathLines.value[popupmenu.relation.uid]);
});
</script>

<style lang="scss">
.net-relations {
  position: relative;
  overflow: visible;

  --net-color-relation-standard: v-bind("pathColors.standard");
  --net-color-relation-active: v-bind("pathColors.active");
  --net-color-relation-active-hue: v-bind("pathColors.activeHue");
  --net-color-relation-faded: v-bind("pathColors.faded");
  --net-color-relation-incoming: v-bind("pathColors.incoming");
  --net-color-relation-outgoing: v-bind("pathColors.outgoing");
  --net-color-relation-connecting: v-bind("pathColors.connecting");

  --net-color-relation-inverse: v-bind("pathColors.inverse");
  --net-color-relation-inverse-active: v-bind("pathColors.inverseActive");
  --net-color-relation-inverse-active-hue: v-bind(
    "pathColors.inverseActiveHue"
  );
  --net-color-relation-inverse-incoming: v-bind("pathColors.inverseIncoming");
  --net-color-relation-inverse-outgoing: v-bind("pathColors.inverseOutgoing");
  --net-color-relation-inverse-connecting: v-bind(
    "pathColors.inverseConnecting"
  );

  --net-color-relation-highlighted: hsl(
    var(--net-color-relation-active-hue),
    100%,
    80%
  );
  --net-color-relation-inverse-highlighted: hsl(
    var(--net-color-relation-inverse-active-hue),
    100%,
    80%
  );

  .-unclickable {
    pointer-events: none;
  }

  &__compactlink {
    stroke: var(--net-color-relation-standard);
    fill: none;
  }
  &__arrow {
    fill: var(--net-color-relation-standard);
  }
}
</style>